import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { CAP, useSession } from "@/auth/session";
import { Chip } from "@/components/Chip";
import { PageHead } from "@/components/PageHead";
import { SearchInput } from "@/components/inputs";
import { ErrorNote } from "@/components/ErrorNote";
import { products, type PermGroupRow, type PermMapRow, type PermRow,
  type SpacePermMapRow } from "@/api/client";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Select } from "@/components/inputs";
import { qk, usePermissionMaps, useSpacePermissions, useSpaces } from "@/modules/products/data";
import { ChoosePrompt, ProductSelect, ScopeBar, ScopeField, ScopeAction } from "@/modules/products/ScopeBar";
import { useKindParam } from "@/lib/hooks/useKindParam";

/** A product's permission vocabulary, and what each SPACE grants of it.
 *
 *  TWO JOBS, ONE SCREEN, SELECTED BY THE SPACE FIELD. Dattatray: *"Each
 *  product shall have its permission map configurable (fixed one time) by
 *  super admin ... fix the permission map once for each space inside of that
 *  product."* The vocabulary and the ticking are the same list of
 *  permissions asked two questions — *what exists* and *what does this space
 *  grant* — so they are one table, not two pages that would drift apart the
 *  first time somebody added a permission to only one of them.
 *
 *    Space = "All spaces"   the vocabulary. Authoring: add a map, a group,
 *                           a permission; deprecate one.
 *    Space = a space        the same rows with a GRANTS tick, and Save /
 *                           Discard at the foot. Authoring is hidden: you
 *                           are answering a different question, and a
 *                           control that edits the vocabulary while you are
 *                           allocating it is how the wrong thing gets
 *                           clicked.
 *
 *  PER (PRODUCT, TOOL), not per product. D-89 recorded "the map goes to kind"
 *  and was incomplete: Cluster carries BOTH a `uds_tool` map (6 groups, 21
 *  permissions) and an `eol_tool` map (1 group, 5). The same integer means
 *  different things in different tools, which is exactly why nothing outside
 *  a product may reason about the numbers.
 *
 *  THE NUMBERS ARE THE CONTRACT. Every deployed bench compares against these
 *  integers, so a permission's id is NOT editable once it exists — changing
 *  what 207 means silently changes what every bench holding it may do, with
 *  no error anywhere, because the tool simply believes the number. Deprecate
 *  and add a new one; the page offers exactly those two actions.
 *
 *  SUPER ADMIN ONLY, per Dattatray. `CAP.MANAGE_PRODUCTS` is held by no plan
 *  and no company role, so a customer sees the vocabulary and no controls.
 */
export function PermissionMapPage() {
  const { can } = useSession();
  const mayAuthor = can(CAP.MANAGE_PRODUCTS);
  const [kind, setKind] = useKindParam();   // ?kind= arrives from the Products drawer
  const [params, setParams] = useSearchParams();
  // ?space= arrives from the Spaces page, so "set what this space grants"
  // starts where the operator was already looking rather than asking them to
  // find the product and the space again.
  const space = params.get("space") ?? "";
  const setSpace = (v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set("space", v); else next.delete("space");
    setParams(next, { replace: true });
  };

  const [tool, setTool] = useState("");
  const q = usePermissionMaps(kind);
  const spacesQ = useSpaces(kind);
  const [addMap, setAddMap] = useState(false);
  const [addGroupTo, setAddGroupTo] = useState<PermMapRow | null>(null);
  const [addPermTo, setAddPermTo] = useState<PermGroupRow | null>(null);

  const spaces = (spacesQ.data?.spaces ?? []).filter((sp) => !sp.archived);
  // A space id left over from another product would be a 404 nobody can
  // explain. Dropped as soon as the product's own list says it is not one.
  useEffect(() => {
    if (space && spacesQ.data && !spaces.some((sp) => sp.space_id === space)) setSpace("");
  }, [space, spacesQ.data, kind]);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products"
        title="Tool permission map"
        desc={space
          ? "What this space grants by default. Every company allocated the space inherits these, and a permission left unticked is one the space does not grant."
          : "The vocabulary a product's tools use, per tool. The numbers are what a bench compares against, so they are never re-pointed — deprecate and add instead."}
      />

      {/* SECOND SELECTOR IS THE TOOL, NOT A MODEL. Dattatray confirmed the
          key: "(product, TOOL) is correct. We decided that we can allocate
          Tool release to a specific model — I think that was the catch (its
          part of tool release rule)." So model targeting lives on RELEASES;
          a map belongs to a product and a tool.

          SPACE SITS BETWEEN THEM because it is the wider cut: it changes the
          QUESTION the table answers, while the tool only narrows the rows. */}
      <ScopeBar right={mayAuthor && !space ? (
        <button className={addMap ? "btn ghost sm" : "btn teal sm"}
          disabled={!kind} title={!kind ? "Choose a product first" : undefined}
          onClick={() => setAddMap(!addMap)}>
          {addMap ? "Cancel" : "Add a tool map"}
        </button>
      ) : undefined}>
        <ProductSelect value={kind} onChange={(k) => { setKind(k); setTool(""); setSpace(""); }} />
        <ScopeField label="Space"
          hint={space ? "tick what this space grants" : "the vocabulary itself"}>
          <Select value={space} onChange={(e) => setSpace(e.target.value)}
            disabled={!kind} style={{ minWidth: 200 }}>
            {/* Not "All spaces" on its own: the reader would expect the union
                of what every space grants, which is a third thing this page
                does not show. */}
            <option value="">All spaces — the vocabulary</option>
            {spaces.map((sp) => (
              <option key={sp.space_id} value={sp.space_id}>{sp.name}</option>
            ))}
          </Select>
        </ScopeField>
        <ScopeField label="Tool"
          hint="the same number means different things in different tools">
          <Select value={tool} onChange={(e) => setTool(e.target.value)}
            disabled={!kind} style={{ minWidth: 200 }}>
            <option value="">All tools</option>
            {(q.data?.maps ?? []).map((m) => (
              <option key={m.map_id} value={m.tool}>{m.name}</option>
            ))}
          </Select>
        </ScopeField>
      </ScopeBar>

      {addMap && !space && <AddMapRow kind={kind} onDone={() => setAddMap(false)} />}

      {!kind && <ChoosePrompt what="permission map" />}
      {kind && space && (
        <SpaceGrants kind={kind} spaceId={space} tool={tool} mayAuthor={mayAuthor} />
      )}
      {kind && !space && q.isLoading && <div className="panel panel-pad cap">Loading…</div>}
      {kind && !space && !q.isLoading && (q.data?.maps.length ?? 0) === 0 && (
        <div className="panel panel-pad cap">
          No permission map for this product yet.
        </div>
      )}

      {/* ONE TABLE PER MAP — `DataTable`, the control every other list page
          in this module uses (Spaces, Tool updates, Products, Organisations).
          This page had been rendering its own three-level nest: a panel per
          map, a bold line per group, a hand-built row per permission with a
          `marginLeft: 14` indent. It was the only list in the console that
          did not look like a list, which is what Dattatray kept reporting as
          "no consistency with Orbit other subpages".

          The GROUP becomes a column rather than a nesting level. That loses
          nothing — a group is an attribute of a permission, not a container
          with its own behaviour — and it gains sorting, paging, alignment and
          the empty state for free. */}
      {!space && (q.data?.maps ?? []).filter((m) => !tool || m.tool === tool).map((m) => (
        <div key={m.map_id}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <div className="section-label">{m.name}</div>
            <span className="mono cap">{m.tool}</span>
            <Chip tone="mute">{m.permission_count} permissions</Chip>
            {mayAuthor && (
              <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                <button className="btn ghost sm"
                  onClick={() => setAddGroupTo(addGroupTo?.map_id === m.map_id ? null : m)}>
                  {addGroupTo?.map_id === m.map_id ? "Cancel" : "Add group"}
                </button>
                <button className="btn ghost sm" disabled={!m.groups.length}
                  title={!m.groups.length ? "Add a group first - a permission lives in one" : undefined}
                  onClick={() => setAddPermTo(
                    addPermTo && m.groups.some((g) => g.group_uid === addPermTo.group_uid)
                      ? null : m.groups[0])}>
                  {addPermTo && m.groups.some((g) => g.group_uid === addPermTo.group_uid)
                    ? "Cancel" : "Add permission"}
                </button>
              </span>
            )}
          </div>

          {addGroupTo?.map_id === m.map_id && (
            <AddGroupRow kind={kind} map={m} onDone={() => setAddGroupTo(null)} />
          )}
          {addPermTo && m.groups.some((g) => g.group_uid === addPermTo.group_uid) && (
            <AddPermissionRow kind={kind} group={addPermTo}
              onDone={() => setAddPermTo(null)} />
          )}

          <DataTable
            rows={m.groups.flatMap((g) => g.permissions.map((perm) => ({
              ...perm, group_name: g.name, group_id: g.group_id,
              group_deprecated: g.deprecated,
            })))}
            cols={permCols(kind, mayAuthor)}
            rowKey={(r) => r.perm_uid ?? `${r.permission_id}`}
            empty={<div className="panel panel-pad cap">
              No permissions in this map yet.
            </div>} />
        </div>
      ))}

    </div>
  );
}

/** What ONE space grants — the vocabulary with a tick on each row (D-102).
 *
 *  `space_definition_permissions` arrived in the Fabric port with 64 rows and
 *  was touched by exactly one query, a COUNT. Nothing read the values and
 *  nothing wrote them, so this — the screen Dattatray described as the whole
 *  point of spaces — could not be built.
 *
 *  THE SAVE REPLACES THE SET, and the foot says so in words, because the
 *  alternative reading is invisible: if an untick meant "leave as it was",
 *  nothing on this screen could ever revoke, and the operator would have no
 *  way to tell which of the two had happened.
 *
 *  ACTIONS AT THE FOOT. Dattatray, on the spec editor: *"we should have Save
 *  Spec and Discard button at the bottom instead of top"* — you finish
 *  ticking at the bottom of a long list, and a button at the top means
 *  scrolling back to a control you have stopped looking at.
 */
function SpaceGrants({ kind, spaceId, tool, mayAuthor }: {
  kind: string; spaceId: string; tool: string; mayAuthor: boolean;
}) {
  const qc = useQueryClient();
  const q = useSpacePermissions(kind, spaceId);
  /** Only what the operator CHANGED. Seeding a full draft from the server
   *  would make every later refetch look like a pending edit. */
  const [edits, setEdits] = useState<Record<string, boolean>>({});
  useEffect(() => { setEdits({}); }, [kind, spaceId]);

  const maps: SpacePermMapRow[] = useMemo(
    () => (q.data?.maps ?? []).filter((m) => !tool || m.tool === tool),
    [q.data, tool]);

  const granted = (p: { perm_uid: string; default: boolean }) =>
    edits[p.perm_uid] ?? p.default;
  const dirty = Object.entries(edits).filter(([uid, on]) => {
    for (const m of q.data?.maps ?? []) {
      for (const g of m.groups) {
        for (const p of g.permissions) if (p.perm_uid === uid) return p.default !== on;
      }
    }
    return false;
  });

  const save = useMutation({
    mutationFn: () => {
      // ONLY THE ONES THAT ARE ON. A row storing `false` is indistinguishable
      // in meaning from no row, and storing it would make the Spaces page's
      // "grants N" count decisions rather than permissions.
      const on: Record<string, boolean> = {};
      for (const m of q.data?.maps ?? []) {
        for (const g of m.groups) {
          for (const p of g.permissions) if (granted(p)) on[p.perm_uid] = true;
        }
      }
      return products.setSpacePermissions(kind, spaceId, on);
    },
    onSuccess: () => {
      setEdits({});
      void qc.invalidateQueries({ queryKey: qk.spacePerms(kind, spaceId) });
      void qc.invalidateQueries({ queryKey: qk.spaces(kind) });
    },
  });

  if (q.isLoading) return <div className="panel panel-pad cap">Loading…</div>;
  if (!q.data) return <div className="panel panel-pad cap">This space is not part of this product.</div>;
  if (!maps.length) {
    return <div className="panel panel-pad cap">
      This product has no permission map yet, so there is nothing for a space to grant.
    </div>;
  }

  const total = maps.reduce((n, m) => n + m.groups.reduce(
    (k, g) => k + g.permissions.filter(granted).length, 0), 0);

  return (
    <>
      {maps.map((m) => (
        <div key={m.map_id}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <div className="section-label">{m.name}</div>
            <span className="mono cap">{m.tool}</span>
            <Chip tone="mute">
              {m.groups.reduce((k, g) => k + g.permissions.filter(granted).length, 0)}
              {" of "}{m.permission_count} granted
            </Chip>
          </div>
          <DataTable
            rows={m.groups.flatMap((g) => g.permissions.map((perm) => ({
              ...perm, group_name: g.name, group_id: g.group_id,
              group_deprecated: g.deprecated,
            })))}
            cols={grantCols(mayAuthor, granted,
              (uid, on) => setEdits((e) => ({ ...e, [uid]: on })))}
            rowKey={(r) => r.perm_uid}
            empty={<div className="panel panel-pad cap">No permissions in this map yet.</div>} />
        </div>
      ))}

      <div className="panel panel-pad" style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 12,
      }}>
        <span className="cap">
          {total} granted{dirty.length ? ` · ${dirty.length} unsaved` : ""}
        </span>
        <span className="cap" style={{ color: "var(--t-dim)" }}>
          Saving replaces the whole set — an unticked permission is removed.
        </span>
        {mayAuthor && (
          <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button className="btn ghost sm" disabled={!dirty.length || save.isPending}
              onClick={() => setEdits({})}>Discard</button>
            <button className="btn teal sm" disabled={!dirty.length || save.isPending}
              onClick={() => save.mutate()}>
              {save.isPending ? "Saving…" : "Save what this space grants"}
            </button>
          </span>
        )}
        <ErrorNote error={save.error} panel />
      </div>
    </>
  );
}

type GrantFlat = PermRow & {
  default: boolean; group_name: string; group_id: number; group_deprecated: boolean;
};

function grantCols(mayAuthor: boolean,
                   granted: (p: { perm_uid: string; default: boolean }) => boolean,
                   set: (uid: string, on: boolean) => void): DataCol<GrantFlat>[] {
  return [
    // FIRST COLUMN, because it is the answer the page exists to give and a
    // column of ticks is read down, not hunted for at the right-hand edge.
    { key: "on", label: "Grants", width: 80,
      render: (r) => (
        <label style={{ display: "flex", justifyContent: "center" }}
          title={r.deprecated && !granted(r)
            ? "Deprecated — it can be removed from a space, not added to one"
            : undefined}>
          <input type="checkbox" checked={granted(r)}
            // A DEPRECATED permission can be taken AWAY but not newly given.
            // Granting one would hand a bench a number the product has
            // already stopped meaning anything by.
            disabled={!mayAuthor || (r.deprecated && !granted(r))}
            onChange={(e) => set(r.perm_uid, e.target.checked)} />
        </label>
      ) },
    { key: "group", label: "Group", width: 190,
      render: (r) => (
        <span style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
          {r.group_name}
          {r.group_deprecated && <Chip tone="mute">deprecated</Chip>}
        </span>
      ) },
    { key: "gid", label: "Group no.", width: 90, mono: true, render: (r) => r.group_id },
    { key: "pid", label: "Number", width: 90, mono: true, render: (r) => r.permission_id },
    { key: "name", label: "Permission", width: 260,
      render: (r) => (
        <span style={{ textDecoration: r.deprecated ? "line-through" : undefined,
          color: r.deprecated ? "var(--t-dim)" : undefined }}>{r.name}</span>
      ) },
  ];
}

/** One flat row per permission — the group is a COLUMN, not a nesting level.
 *  A group is an attribute of a permission, not a container with behaviour of
 *  its own, so flattening loses nothing and gains sorting, alignment, paging
 *  and the empty state that `DataTable` already provides. */
type PermFlat = PermRow & {
  group_name: string; group_id: number; group_deprecated: boolean;
};

function permCols(kind: string, mayAuthor: boolean): DataCol<PermFlat>[] {
  return [
    { key: "group", label: "Group", width: 190,
      render: (r) => (
        <span style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
          {r.group_name}
          {r.group_deprecated && <Chip tone="mute">deprecated</Chip>}
        </span>
      ) },
    { key: "gid", label: "Group no.", width: 90, mono: true, render: (r) => r.group_id },
    { key: "pid", label: "Number", width: 90, mono: true, render: (r) => r.permission_id },
    { key: "name", label: "Permission", width: 260,
      render: (r) => (
        <span style={{ textDecoration: r.deprecated ? "line-through" : undefined,
          color: r.deprecated ? "var(--t-dim)" : undefined }}>{r.name}</span>
      ) },
    { key: "state", label: "", width: 110,
      render: (r) => (r.deprecated ? <Chip tone="mute">deprecated</Chip> : <Chip tone="ok">live</Chip>) },
    ...(mayAuthor ? [{
      key: "act", label: "", width: 120,
      render: (r: PermFlat) => <PermissionAction kind={kind} perm={r} />,
    }] : []),
  ];
}

function PermissionAction({ kind, perm }: {
  kind: string; perm: PermRow;
}) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () => products.updatePermission(kind, perm.perm_uid ?? "",
      { deprecated: !perm.deprecated }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.maps(kind) }),
  });
  if (!perm.perm_uid) return null;
  return (
    <button className="btn ghost sm"
      disabled={toggle.isPending} onClick={() => toggle.mutate()}>
      {perm.deprecated ? "Restore" : "Deprecate"}
    </button>
  );
}

/** The three authoring forms, all INLINE. Dattatray: "No Pop up."
 *
 *  A modal was wrong here for a specific reason, not just a style one: each
 *  of these adds something INTO a thing already on screen — a map into a
 *  product, a group into a map, a permission into a group. Covering that
 *  thing with a dialog hides the context the number has to be unique within,
 *  which is exactly what the operator needs to see while typing it.
 */
function InlineForm({ children, onCancel, onGo, go, busy, err }: {
  children: React.ReactNode; onCancel: () => void; onGo: () => void;
  go: string; busy: boolean; err: unknown;
}) {
  return (
    <div className="panel panel-pad" style={{
      display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
      marginBottom: 12,
    }}>
      {children}
      <ScopeAction>
        <span style={{ display: "flex", gap: 8 }}>
          <button className="btn teal sm" disabled={busy} onClick={onGo}>
            {busy ? "Saving…" : go}
          </button>
          <button className="btn ghost sm" onClick={onCancel}>Cancel</button>
        </span>
      </ScopeAction>
      {err ? (
        <div className="panel panel-pad" style={{ flexBasis: "100%" }}>
          {(err as Error).message}
        </div>
      ) : null}
    </div>
  );
}

function AddMapRow({ kind, onDone }: { kind: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [tool, setTool] = useState("");
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: () => products.addMap(kind, { tool, name: name || undefined }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.maps(kind) });
      void qc.invalidateQueries({ queryKey: qk.products });
      onDone();
    },
  });
  return (
    <InlineForm onCancel={onDone} onGo={() => add.mutate()} go="Add map"
      busy={!tool || add.isPending} err={add.error}>
      <ScopeField label="Tool slug" hint="what the tool identifies itself as">
        <SearchInput value={tool} autoFocus mono placeholder="uds_tool"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setTool(e.target.value.toLowerCase().replace(/\s+/g, "_"))} />
      </ScopeField>
      <ScopeField label="Name">
        <SearchInput value={name} placeholder="UDS Tool"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
      </ScopeField>
    </InlineForm>
  );
}

function AddGroupRow({ kind, map, onDone }: {
  kind: string; map: PermMapRow; onDone: () => void;
}) {
  const qc = useQueryClient();
  const [gid, setGid] = useState("");
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: () => products.addGroup(kind, map.map_id, { group_id: Number(gid), name }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.maps(kind) });
      onDone();
    },
  });
  return (
    <InlineForm onCancel={onDone} onGo={() => add.mutate()} go="Add group"
      busy={!/^\d+$/.test(gid) || !name || add.isPending} err={add.error}>
      <ScopeField label="Group number" hint={`unique within ${map.tool}`}>
        <SearchInput value={gid} autoFocus mono placeholder="200" style={{ width: 100 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGid(e.target.value)} />
      </ScopeField>
      <ScopeField label="Name">
        <SearchInput value={name} placeholder="Flashing"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
      </ScopeField>
    </InlineForm>
  );
}

function AddPermissionRow({ kind, group, onDone }: {
  kind: string; group: PermGroupRow; onDone: () => void;
}) {
  const qc = useQueryClient();
  const [pid, setPid] = useState("");
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: () => products.addPermission(kind, group.group_uid ?? "",
      { permission_id: Number(pid), name }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.maps(kind) });
      onDone();
    },
  });
  return (
    <InlineForm onCancel={onDone} onGo={() => add.mutate()} go="Add permission"
      busy={!/^\d+$/.test(pid) || !name || add.isPending} err={add.error}>
      <ScopeField label="Permission number"
        hint="unique across the WHOLE map — a bench gets a flat set. Permanent.">
        <SearchInput value={pid} autoFocus mono placeholder="207" style={{ width: 100 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPid(e.target.value)} />
      </ScopeField>
      <ScopeField label="Name">
        <SearchInput value={name} placeholder="Write DID"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
      </ScopeField>
    </InlineForm>
  );
}
